/**
 * Map Firebase Auth errors to safe, user-facing copy.
 * Never return Firebase's raw err.message.
 */
export function mapAuthError(err, context = 'signIn') {
  let code = err?.code || '';
  if (!code && typeof err?.message === 'string') {
    const match = err.message.match(/auth\/[a-z0-9-]+/i);
    if (match) code = match[0].toLowerCase();
  }

  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      if (context === 'reset') {
        return "If an account exists for that email, you'll receive reset instructions.";
      }
      return 'Email or password is incorrect. Please try again.';
    case 'auth/email-already-in-use':
      return 'That email is already registered. Try signing in, or reset your password.';
    case 'auth/weak-password':
      return 'Use a password with at least 6 characters.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    case 'auth/missing-email':
      return 'Please enter your email address.';
    case 'auth/missing-name':
      return 'Please enter your first name.';
    default:
      if (context === 'signUp') {
        return "We couldn't create your account. Please try again.";
      }
      if (context === 'reset') {
        return "We couldn't send a reset email. Please try again.";
      }
      return "We couldn't sign you in. Please try again.";
  }
}

/**
 * Map chat/stream failures to safe assistant copy.
 * Never surface env vars, vendor names, or upstream bodies.
 */
// When an answer fails, the visitor may be someone who needed it badly. Keep
// the way to a real person open on every failure that isn't a sign-in issue.
// (ChatParts' CrisisLine says the same words, with 988 as a link.)
const STILL_HERE = ' If you need to talk to someone right now, call or text 988.';

// xAI refused the church's key (out of credit): functions/chat.js says so in
// these words, with code "xai_blocked".
const RESTING = 'AI Rob is resting for a little while. Please try again later, or reach a real person at The Net.';

// Failures the server reports in its own words (functions/chat.js): xAI down
// or out of credit, the function unavailable or failing, no reply. Each gets
// the 988 line, and so do the daily ceiling ("reached its capacity for
// today", a 503) and the network cap, which turn a person away for hours
// whatever they wrote. The other limits and sign-in messages (daily_limit,
// window_limit, email_gate, concurrent, message_too_long, unauthenticated)
// stay as sent; a crisis message any limit refuses brings the full crisis
// card as well (ChatView).
const WITH_988 = new Set(['upstream', 'unavailable', 'no_content', 'internal', 'xai_blocked', 'service_budget', 'ip_limit']);

function withStillHere(text) {
  return /\b988\b/.test(text) ? text : text + STILL_HERE;
}

export function mapChatError(error) {
  if (error?.code === 'xai_blocked') {
    return withStillHere(error.safeMessage || RESTING);
  }

  // Server-provided messages (rate limits, daily capacity, crisis-safe copy)
  // are pre-sanitized and marked safe by utils/api.js — show them verbatim,
  // with the 988 line where WITH_988 (or an uncoded 5xx) says so.
  if (error?.safeMessage) {
    const keepDoorOpen = WITH_988.has(error.code) || (!error.code && Number(error.status) >= 500);
    return keepDoorOpen ? withStillHere(error.safeMessage) : error.safeMessage;
  }

  const msg = String(error?.message || error || '').toLowerCase();

  if (
    msg.includes('not authenticated') ||
    msg.includes('session expired') ||
    msg.includes('sign in')
  ) {
    return 'Your session expired. Please sign in again.';
  }

  if (
    msg.includes('network') ||
    msg.includes('failed to fetch') ||
    msg.includes('load failed')
  ) {
    return "We couldn't reach the server. Check your connection and try again." + STILL_HERE;
  }

  // Backend already sends sanitized strings — pass through known safe ones
  const safeBackend = String(error?.message || '');
  if (
    safeBackend === 'The chat service is temporarily unavailable. Please try again.' ||
    safeBackend === 'The AI service had a problem. Please try again.' ||
    safeBackend === 'No reply was received. Please try again.' ||
    safeBackend === 'Something went wrong. Please try again.'
  ) {
    return safeBackend + STILL_HERE;
  }

  return 'Something went wrong while generating a reply. Please try again.' + STILL_HERE;
}
